import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Gift, Star } from "lucide-react";
import { toast } from "sonner";

import { guestRewardsApi } from "@/api/guest/guestRewardsApi.js";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { useSession } from "@/hooks/useSession.js";
import {
  formatGuestDate,
  formatGuestDateTime,
  formatMoney,
  formatTransactionType,
  getApiData,
  getApiMessage,
  read,
} from "@/utils/guestRewardsFormat.js";
import {
  FullScreenLoading,
  GuestPageShell,
  LoadingBlock,
  PageHeader,
  RegistrationPrompt,
  SummaryCard,
} from "@/pages/guest/GuestProfilePage.jsx";

const GuestLoyaltyPage = () => {
  const navigate = useNavigate();
  const { session, hotel, room, loading: sessionLoading } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionLoading && !session) navigate("/guest/expired", { replace: true });
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    const loadLoyalty = async () => {
      if (!session) return;
      try {
        setLoading(true);
        const response = await guestRewardsApi.getLoyalty();
        setData(getApiData(response));
      } catch (error) {
        toast.error(getApiMessage(error, "Unable to load loyalty points."));
      } finally {
        setLoading(false);
      }
    };
    loadLoyalty();
  }, [session]);

  if (sessionLoading) return <FullScreenLoading />;
  if (!session) return null;

  const account = read(data, "account", "Account");
  const program = read(data, "program", "Program");
  const transactions = read(data, "transactions", "Transactions", []);
  const requiresRegistration = read(data, "requiresRegistration", "RequiresRegistration", true);

  return (
    <>
      <Helmet>
        <title>Loyalty - Guest Portal</title>
      </Helmet>
      <GuestLayout hotel={hotel} room={room} session={session}>
        <GuestPageShell onBack={() => navigate("/guest/home")}>
          <PageHeader icon={Gift} title="Loyalty points" subtitle="Track your points, tier, and reward history." />
          {loading ? (
            <LoadingBlock text="Loading loyalty points..." />
          ) : requiresRegistration || !account ? (
            <RegistrationPrompt onRegister={() => navigate("/guest/register")} />
          ) : (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <SummaryCard label="Available points" value={read(account, "availablePoints", "AvailablePoints", 0)} icon={Gift} />
                <SummaryCard label="Lifetime points" value={read(account, "lifetimePoints", "LifetimePoints", 0)} icon={Star} />
                <SummaryCard label="Tier" value={read(account, "currentTierName", "CurrentTierName", "Not assigned")} />
                <SummaryCard label="Total stays" value={read(data, "totalStays", "TotalStays", 0)} />
                <SummaryCard label="Last completed stay" value={formatGuestDate(read(data, "lastStayAt", "LastStayAt"))} />
                <SummaryCard label="Total paid spend" value={formatMoney(read(data, "totalSpent", "TotalSpent", 0))} />
              </div>
              {program ? (
                <div className="rounded-2xl border bg-white p-4 text-sm text-slate-600">
                  <p className="font-semibold text-slate-900">{read(program, "name", "Name", "Hotel loyalty program")}</p>
                  <p className="mt-1">
                    Earn points from eligible hotel spending. Points can be redeemed when the hotel allows it.
                  </p>
                </div>
              ) : null}
              <section>
                <h2 className="mb-3 text-lg font-bold text-slate-900">Points history</h2>
                {transactions.length === 0 ? (
                  <EmptyState text="No loyalty transactions recorded yet." />
                ) : (
                  <div className="space-y-3">
                    {transactions.map((transaction) => (
                      <div key={read(transaction, "id", "Id")} className="rounded-2xl border bg-white p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-900">
                              {formatTransactionType(read(transaction, "transactionType", "TransactionType"))}
                            </p>
                            <p className="mt-1 text-sm text-slate-500">
                              {read(transaction, "description", "Description", "Loyalty transaction")}
                            </p>
                          </div>
                          <p className="font-bold text-[#923536]">{read(transaction, "points", "Points", 0)} pts</p>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>{formatMoney(read(transaction, "amount", "Amount", 0))}</span>
                          <span>{formatGuestDateTime(read(transaction, "createdAt", "CreatedAt"))}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </GuestPageShell>
      </GuestLayout>
    </>
  );
};

const EmptyState = ({ text }) => (
  <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-slate-600">{text}</div>
);

export default GuestLoyaltyPage;
